import { useLocation } from "react-router-dom";

/**
 * Единственный маппинг «ключ страницы (App.tsx::pageComponents)» ↔ «адрес».
 *
 * Источник истины для навигации кабинета — адресная строка, а не стор:
 * поэтому и роутер, и сайдбар, и `setCurrentPage` обязаны договариваться
 * через этот файл. Если завести второй маппинг, подсветка меню и адрес
 * неизбежно разъедутся.
 */

/** Страница по умолчанию — на неё уходят «/» и неизвестные адреса. */
export const DEFAULT_PAGE = "complex_check";
export const DEFAULT_PATH = "/check/person";

/** Ключи страниц отчётов: их адрес несёт идентификатор задачи (job_id). */
export const PERSON_REPORT_PAGE = "complex_report";
export const LEGAL_REPORT_PAGE = "complex_legal_report";

export const PERSON_REPORT_BASE = "/reports";
export const LEGAL_REPORT_BASE = "/reports/legal";
export const MODULE_BASE = "/modules";

/**
 * Сценарные экраны с человекочитаемыми адресами. Всё остальное (60+ страниц
 * отдельных модулей) живёт под `/modules/:key` — переименовывать ключи нельзя,
 * они используются в истории проверок и уведомлениях.
 */
const PAGE_TO_PATH: Readonly<Record<string, string>> = {
  complex_check: DEFAULT_PATH,
  company_check: "/check/org",
  vehicle_check: "/check/vehicle",
  crypto_check: "/check/crypto",
  fizur: "/fizur",
  history: "/history",
  monitoring: "/monitoring",
  modules_catalog: MODULE_BASE,
};

const PATH_TO_PAGE: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(PAGE_TO_PATH).map(([page, path]) => [path, page]),
);

/** Идентификаторы отчётов, которые подставляются в адрес страниц отчётов. */
export interface ReportJobContext {
  reportJobId?: string | null;
  legalReportJobId?: string | null;
}

/**
 * Адрес для ключа страницы. Для отчётов нужен job_id: без него остаётся
 * «голый» /reports — страница отчёта сама покажет «нет активного отчёта».
 */
export function pathForPage(page: string, jobs: ReportJobContext = {}): string {
  const direct = PAGE_TO_PATH[page];
  if (direct) return direct;

  if (page === PERSON_REPORT_PAGE) {
    return jobs.reportJobId ? `${PERSON_REPORT_BASE}/${jobs.reportJobId}` : PERSON_REPORT_BASE;
  }
  if (page === LEGAL_REPORT_PAGE) {
    return jobs.legalReportJobId ? `${LEGAL_REPORT_BASE}/${jobs.legalReportJobId}` : LEGAL_REPORT_BASE;
  }

  return `${MODULE_BASE}/${page}`;
}

/** Ключ страницы по адресу. Неизвестный адрес трактуем как страницу по умолчанию. */
export function pageForPath(pathname: string): string {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  const direct = PATH_TO_PAGE[path];
  if (direct) return direct;

  // Порядок важен: /reports/legal/... — частный случай /reports/...
  if (path === LEGAL_REPORT_BASE || path.startsWith(`${LEGAL_REPORT_BASE}/`)) return LEGAL_REPORT_PAGE;
  if (path === PERSON_REPORT_BASE || path.startsWith(`${PERSON_REPORT_BASE}/`)) return PERSON_REPORT_PAGE;

  if (path === MODULE_BASE) return "modules_catalog";
  if (path.startsWith(`${MODULE_BASE}/`)) {
    const key = path.slice(MODULE_BASE.length + 1).split("/")[0];
    return key || DEFAULT_PAGE;
  }

  return DEFAULT_PAGE;
}

/**
 * Канонический адрес ключа, у которого он есть. Нужен, чтобы `/modules/history`
 * и подобные «вторые адреса» одной страницы уводили на канон, а не плодили
 * дубли ссылок.
 */
export function canonicalPathForModuleKey(key: string): string | null {
  if (PAGE_TO_PATH[key]) return PAGE_TO_PATH[key];
  if (key === PERSON_REPORT_PAGE) return PERSON_REPORT_BASE;
  if (key === LEGAL_REPORT_PAGE) return LEGAL_REPORT_BASE;
  return null;
}

/**
 * Активная страница по адресу. Подсветка меню считается именно так —
 * чтобы она была верна сразу при загрузке по прямой ссылке, до того как
 * стор успеет синхронизироваться.
 */
export function useActivePageKey(): string {
  const { pathname } = useLocation();
  return pageForPath(pathname);
}
